<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import BillingAiTopUpModal from 'dashboard/components-next/billing/BillingAiTopUpModal.vue';
import LlmLogsAPI from 'dashboard/api/captain/llmLogs';
import AiAgentRunsAPI from 'dashboard/api/aiAgents/runs';
import BillingAPI from 'dashboard/api/billing';

const { t } = useI18n();

const aiCreditsBalance = ref(0);
const purchasedAiCreditsBalance = ref(0);
const creditsUsedThisPeriod = ref(0);
const topUpModalRef = ref(null);

// ── tab ──────────────────────────────────────────────────────────────────────
const activeTab = ref('captain');

// ── Captain state ─────────────────────────────────────────────────────────────
const emptyCaptainFilters = () => ({
  feature: '',
  model: '',
  conversation_display_id: '',
  created_after: '',
  created_before: '',
});

const captainLoading = ref(true);
const captainRows = ref([]);
const captainFilterOptions = ref({ features: [], models: [] });
const captainDraft = ref(emptyCaptainFilters());
const captainActive = ref(emptyCaptainFilters());

const captainMeta = ref({
  current_page: 1,
  total_pages: 1,
  total_count: 0,
  per_page: 25,
  totals: {
    prompt_tokens: 0,
    completion_tokens: 0,
    total_tokens: 0,
    request_count: 0,
    total_ai_credits: 0,
  },
});

// ── AI Agent state ────────────────────────────────────────────────────────────
const emptyAgentFilters = () => ({
  model: '',
  ai_agent_id: '',
  mode: '',
  created_after: '',
  created_before: '',
});

const agentLoading = ref(true);
const agentRows = ref([]);
const agentFilterOptions = ref({ models: [], agents: [] });
const agentDraft = ref(emptyAgentFilters());
const agentActive = ref(emptyAgentFilters());

const agentMeta = ref({
  current_page: 1,
  total_pages: 1,
  total_count: 0,
  per_page: 25,
  totals: {
    prompt_tokens: 0,
    completion_tokens: 0,
    total_tokens: 0,
    request_count: 0,
    total_ai_credits: 0,
  },
});

// ── Shared helpers ────────────────────────────────────────────────────────────
const formatDateTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
};

const formatInt = n =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString() : '0';

const formatCreditsBalance = val => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const formatCredits = val => {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_CREDITS_UNIT')}`;
};

const paginationLabel = (meta, key) =>
  t(key, { current: meta.current_page, total: meta.total_pages });

// ── Captain logic ─────────────────────────────────────────────────────────────
const captainHasActiveFilters = computed(() =>
  Object.values(captainActive.value).some(v => String(v ?? '').trim() !== '')
);

const applyCaptainResponse = data => {
  captainRows.value = data.payload || [];
  captainMeta.value = {
    current_page: data.meta?.current_page ?? 1,
    total_pages: data.meta?.total_pages ?? 1,
    total_count: data.meta?.total_count ?? 0,
    per_page: data.meta?.per_page ?? 25,
    totals: {
      prompt_tokens: data.meta?.totals?.prompt_tokens ?? 0,
      completion_tokens: data.meta?.totals?.completion_tokens ?? 0,
      total_tokens: data.meta?.totals?.total_tokens ?? 0,
      request_count: data.meta?.totals?.request_count ?? 0,
      total_ai_credits: data.meta?.totals?.total_ai_credits ?? 0,
    },
  };
  if (data.meta?.filter_options) {
    captainFilterOptions.value = {
      features: data.meta.filter_options.features || [],
      models: data.meta.filter_options.models || [],
    };
  }
};

const fetchCaptainPage = async page => {
  captainLoading.value = true;
  try {
    const { data } = await LlmLogsAPI.index({ page, ...captainActive.value });
    applyCaptainResponse(data);
  } catch {
    useAlert(t('GENERAL_SETTINGS.AI_BILLING_PAGE.LOAD_ERROR'));
    captainRows.value = [];
  } finally {
    captainLoading.value = false;
  }
};

const applyCaptainFilters = () => {
  captainActive.value = { ...captainDraft.value };
  fetchCaptainPage(1);
};

const resetCaptainFilters = () => {
  captainDraft.value = emptyCaptainFilters();
  captainActive.value = emptyCaptainFilters();
  fetchCaptainPage(1);
};

const captainPrev = () => {
  if (captainMeta.value.current_page <= 1) return;
  fetchCaptainPage(captainMeta.value.current_page - 1);
};

const captainNext = () => {
  if (captainMeta.value.current_page >= captainMeta.value.total_pages) return;
  fetchCaptainPage(captainMeta.value.current_page + 1);
};

// ── AI Agent logic ────────────────────────────────────────────────────────────
const agentHasActiveFilters = computed(() =>
  Object.values(agentActive.value).some(v => String(v ?? '').trim() !== '')
);

const applyAgentResponse = data => {
  agentRows.value = data.payload || [];
  agentMeta.value = {
    current_page: data.meta?.current_page ?? 1,
    total_pages: data.meta?.total_pages ?? 1,
    total_count: data.meta?.total_count ?? 0,
    per_page: data.meta?.per_page ?? 25,
    totals: {
      prompt_tokens: data.meta?.totals?.prompt_tokens ?? 0,
      completion_tokens: data.meta?.totals?.completion_tokens ?? 0,
      total_tokens: data.meta?.totals?.total_tokens ?? 0,
      request_count: data.meta?.totals?.request_count ?? 0,
      total_ai_credits: data.meta?.totals?.total_ai_credits ?? 0,
    },
  };
  if (data.meta?.filter_options) {
    agentFilterOptions.value = {
      models: data.meta.filter_options.models || [],
      agents: data.meta.filter_options.agents || [],
    };
  }
};

const fetchAgentPage = async page => {
  agentLoading.value = true;
  try {
    const { data } = await AiAgentRunsAPI.index({ page, ...agentActive.value });
    applyAgentResponse(data);
  } catch {
    useAlert(t('GENERAL_SETTINGS.AI_BILLING_PAGE.LOAD_ERROR'));
    agentRows.value = [];
  } finally {
    agentLoading.value = false;
  }
};

const applyAgentFilters = () => {
  agentActive.value = { ...agentDraft.value };
  fetchAgentPage(1);
};

const resetAgentFilters = () => {
  agentDraft.value = emptyAgentFilters();
  agentActive.value = emptyAgentFilters();
  fetchAgentPage(1);
};

const agentPrev = () => {
  if (agentMeta.value.current_page <= 1) return;
  fetchAgentPage(agentMeta.value.current_page - 1);
};

const agentNext = () => {
  if (agentMeta.value.current_page >= agentMeta.value.total_pages) return;
  fetchAgentPage(agentMeta.value.current_page + 1);
};

const openTopUpModal = () => {
  topUpModalRef.value?.open();
};

const loadAiCredits = async () => {
  try {
    const { data } = await BillingAPI.getAiCredits();
    aiCreditsBalance.value = data.included_balance ?? data.balance ?? 0;
    purchasedAiCreditsBalance.value =
      data.purchased_balance ?? data.purchased_ai_credits_balance ?? 0;
    const used = (data.transactions || [])
      .filter(txn => txn.transaction_type === 'usage_deduction')
      .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
    creditsUsedThisPeriod.value = used;
  } catch {
    /* balance unavailable */
  }
};

onMounted(() => {
  loadAiCredits();
  fetchCaptainPage(1);
  fetchAgentPage(1);
});
</script>

<template>
  <div class="flex w-full max-w-none min-w-0 flex-col gap-6">
    <BaseSettingsHeader
      :title="t('GENERAL_SETTINGS.AI_BILLING_PAGE.TITLE')"
      :description="t('GENERAL_SETTINGS.AI_BILLING_PAGE.DESCRIPTION')"
    />

    <div class="flex w-full min-w-0 flex-col gap-6 px-8 md:px-10">
      <div
        class="grid gap-4 rounded-xl border border-solid border-n-weak bg-n-surface-1 p-5 sm:grid-cols-3"
      >
        <div>
          <p class="text-xs font-medium uppercase text-n-slate-11">
            {{
              t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_CREDITS_INCLUDED_TITLE')
            }}
          </p>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ formatCreditsBalance(aiCreditsBalance) }}
            {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_CREDITS_UNIT') }}
          </p>
        </div>
        <div>
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p class="text-xs font-medium uppercase text-n-slate-11">
              {{
                t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_CREDITS_PURCHASED_TITLE')
              }}
            </p>
            <button
              type="button"
              class="m-0 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-xs font-medium text-n-blue-11 hover:underline"
              @click="openTopUpModal"
            >
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.BUY_CREDITS') }}
            </button>
          </div>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ formatCreditsBalance(purchasedAiCreditsBalance) }}
            {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_CREDITS_UNIT') }}
          </p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase text-n-slate-11">
            {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.CREDITS_USED_RECENT') }}
          </p>
          <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ formatInt(creditsUsedThisPeriod) }}
            {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_CREDITS_UNIT') }}
          </p>
        </div>
      </div>

      <BillingAiTopUpModal ref="topUpModalRef" @updated="loadAiCredits" />

      <!-- Tabs -->
      <div class="flex border-b border-n-weak dark:border-n-slate-6">
        <button
          v-for="tab in ['captain', 'ai_agent']"
          :key="tab"
          class="px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors"
          :class="
            activeTab === tab
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = tab"
        >
          {{
            tab === 'captain'
              ? t('GENERAL_SETTINGS.AI_BILLING_PAGE.TAB_CAPTAIN')
              : t('GENERAL_SETTINGS.AI_BILLING_PAGE.TAB_AI_AGENT')
          }}
        </button>
      </div>

      <!-- ══ CAPTAIN TAB ══════════════════════════════════════════════════════ -->
      <template v-if="activeTab === 'captain'">
        <section
          class="rounded-lg border border-n-weak bg-n-solid-1 p-4 shadow-sm dark:border-n-slate-6"
        >
          <h2 class="text-base font-semibold text-n-slate-12 mb-3">
            <template v-if="captainHasActiveFilters">
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTALS_FILTERED') }}
            </template>
            <template v-else>
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTALS_TITLE') }}
            </template>
          </h2>
          <dl
            class="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-5"
          >
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_REQUESTS') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(captainMeta.totals.request_count) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_PROMPT_TOKENS') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(captainMeta.totals.prompt_tokens) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{
                  t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_COMPLETION_TOKENS')
                }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(captainMeta.totals.completion_tokens) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_TOKENS') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(captainMeta.totals.total_tokens) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_AI_CREDITS') }}
              </dt>
              <dd class="font-medium text-n-slate-12 tabular-nums">
                {{ formatCredits(captainMeta.totals.total_ai_credits) }}
              </dd>
            </div>
          </dl>
        </section>

        <section
          class="rounded-lg border border-n-weak bg-n-solid-1 shadow-sm dark:border-n-slate-6 overflow-hidden"
        >
          <div
            class="px-4 py-3 border-b border-n-weak dark:border-n-slate-6 flex flex-col gap-1"
          >
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.USAGE_LOG_TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTERS_TITLE') }}
            </p>
          </div>

          <div
            class="px-4 py-4 border-b border-n-weak dark:border-n-slate-6 bg-n-alpha-1"
          >
            <div
              class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 items-end"
            >
              <div class="flex flex-col gap-1 min-w-0">
                <label
                  class="text-xs font-medium text-n-slate-11"
                  for="ai-billing-filter-feature"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_FEATURE') }}
                </label>
                <select
                  id="ai-billing-filter-feature"
                  v-model="captainDraft.feature"
                  :disabled="captainLoading"
                  class="h-9 w-full rounded-md border border-n-weak bg-n-solid-2 px-2 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-slate-6"
                >
                  <option value="">
                    {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_ALL') }}
                  </option>
                  <option
                    v-for="f in captainFilterOptions.features"
                    :key="f"
                    :value="f"
                  >
                    {{ f }}
                  </option>
                </select>
              </div>
              <div class="min-w-0">
                <Input
                  v-model="captainDraft.model"
                  type="text"
                  size="sm"
                  :label="t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_MODEL')"
                  :disabled="captainLoading"
                  :placeholder="
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_MODEL')
                  "
                />
              </div>
              <div class="min-w-0">
                <Input
                  v-model="captainDraft.conversation_display_id"
                  type="number"
                  size="sm"
                  :label="
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_CONVERSATION')
                  "
                  :disabled="captainLoading"
                  :placeholder="
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_CONVERSATION')
                  "
                />
              </div>
              <div class="min-w-0">
                <Input
                  v-model="captainDraft.created_after"
                  type="date"
                  size="sm"
                  :label="
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_DATE_FROM')
                  "
                  :disabled="captainLoading"
                />
              </div>
              <div class="min-w-0">
                <Input
                  v-model="captainDraft.created_before"
                  type="date"
                  size="sm"
                  :label="t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_DATE_TO')"
                  :disabled="captainLoading"
                />
              </div>
              <div class="flex flex-wrap gap-2 justify-start xl:justify-end">
                <Button
                  size="sm"
                  :disabled="captainLoading"
                  @click="applyCaptainFilters"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_APPLY') }}
                </Button>
                <Button
                  size="sm"
                  variant="faded"
                  :disabled="captainLoading"
                  @click="resetCaptainFilters"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_RESET') }}
                </Button>
              </div>
            </div>
          </div>

          <div v-if="captainLoading" class="flex justify-center py-10">
            <SpinnerLoader :size="32" class="text-n-blue-9" />
          </div>

          <template v-else>
            <div
              v-if="captainRows.length === 0"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.EMPTY') }}
            </div>

            <div v-else class="overflow-x-auto">
              <table class="w-full min-w-[720px] text-left text-sm">
                <thead
                  class="bg-n-slate-3 text-n-slate-11 dark:bg-n-slate-2 uppercase tracking-wide text-xs"
                >
                  <tr>
                    <th class="px-4 py-2 font-medium">
                      {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_DATE') }}
                    </th>
                    <th class="px-4 py-2 font-medium">
                      {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_FEATURE') }}
                    </th>
                    <th class="px-4 py-2 font-medium">
                      {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_MODEL') }}
                    </th>
                    <th class="px-4 py-2 font-medium">
                      {{
                        t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_CONVERSATION')
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{
                        t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_PROMPT_TOKENS')
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{
                        t(
                          'GENERAL_SETTINGS.AI_BILLING_PAGE.COL_COMPLETION_TOKENS'
                        )
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{
                        t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_TOTAL_TOKENS')
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_AI_CREDITS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak dark:divide-n-slate-6">
                  <tr
                    v-for="row in captainRows"
                    :key="row.id"
                    class="text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    <td class="px-4 py-2 whitespace-nowrap">
                      {{ formatDateTime(row.created_at) }}
                    </td>
                    <td class="px-4 py-2 font-mono text-xs">
                      {{ row.feature }}
                    </td>
                    <td class="px-4 py-2 font-mono text-xs">
                      {{ row.model }}
                    </td>
                    <td class="px-4 py-2 font-mono text-xs">
                      {{
                        row.conversation_display_id == null ||
                        row.conversation_display_id === ''
                          ? '—'
                          : String(row.conversation_display_id)
                      }}
                    </td>
                    <td class="px-4 py-2 text-right tabular-nums">
                      {{ formatInt(row.prompt_tokens) }}
                    </td>
                    <td class="px-4 py-2 text-right tabular-nums">
                      {{ formatInt(row.completion_tokens) }}
                    </td>
                    <td class="px-4 py-2 text-right tabular-nums font-medium">
                      {{ formatInt(row.total_tokens) }}
                    </td>
                    <td
                      class="px-4 py-2 text-right tabular-nums text-n-slate-12"
                    >
                      {{ formatCredits(row.ai_credits_used) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="captainMeta.total_pages > 1"
              class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-n-weak dark:border-n-slate-6"
            >
              <span class="text-xs text-n-slate-11">
                {{
                  paginationLabel(
                    captainMeta,
                    'GENERAL_SETTINGS.AI_BILLING_PAGE.PAGINATION'
                  )
                }}
              </span>
              <div class="flex gap-2">
                <Button
                  size="sm"
                  variant="faded"
                  :disabled="captainMeta.current_page <= 1"
                  @click="captainPrev"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.PREVIOUS') }}
                </Button>
                <Button
                  size="sm"
                  variant="faded"
                  :disabled="
                    captainMeta.current_page >= captainMeta.total_pages
                  "
                  @click="captainNext"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.NEXT') }}
                </Button>
              </div>
            </div>
          </template>
        </section>
      </template>

      <!-- ══ AI AGENT TAB ════════════════════════════════════════════════════ -->
      <template v-else>
        <section
          class="rounded-lg border border-n-weak bg-n-solid-1 p-4 shadow-sm dark:border-n-slate-6"
        >
          <h2 class="text-base font-semibold text-n-slate-12 mb-3">
            <template v-if="agentHasActiveFilters">
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTALS_FILTERED') }}
            </template>
            <template v-else>
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTALS_TITLE') }}
            </template>
          </h2>
          <dl
            class="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-5"
          >
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_REQUESTS') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(agentMeta.totals.request_count) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_PROMPT_TOKENS') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(agentMeta.totals.prompt_tokens) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{
                  t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_COMPLETION_TOKENS')
                }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(agentMeta.totals.completion_tokens) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_TOKENS') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ formatInt(agentMeta.totals.total_tokens) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.TOTAL_AI_CREDITS') }}
              </dt>
              <dd class="font-medium text-n-slate-12 tabular-nums">
                {{ formatCredits(agentMeta.totals.total_ai_credits) }}
              </dd>
            </div>
          </dl>
        </section>

        <section
          class="rounded-lg border border-n-weak bg-n-solid-1 shadow-sm dark:border-n-slate-6 overflow-hidden"
        >
          <div
            class="px-4 py-3 border-b border-n-weak dark:border-n-slate-6 flex flex-col gap-1"
          >
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_SECTION_TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTERS_TITLE') }}
            </p>
          </div>

          <div
            class="px-4 py-4 border-b border-n-weak dark:border-n-slate-6 bg-n-alpha-1"
          >
            <div
              class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 items-end"
            >
              <div class="flex flex-col gap-1 min-w-0">
                <label
                  class="text-xs font-medium text-n-slate-11"
                  for="agent-filter-agent"
                >
                  {{
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_FILTER_AGENT')
                  }}
                </label>
                <select
                  id="agent-filter-agent"
                  v-model="agentDraft.ai_agent_id"
                  :disabled="agentLoading"
                  class="h-9 w-full rounded-md border border-n-weak bg-n-solid-2 px-2 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-slate-6"
                >
                  <option value="">
                    {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_ALL') }}
                  </option>
                  <option
                    v-for="a in agentFilterOptions.agents"
                    :key="a.id"
                    :value="String(a.id)"
                  >
                    {{ a.name }}
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1 min-w-0">
                <label
                  class="text-xs font-medium text-n-slate-11"
                  for="agent-filter-mode"
                >
                  {{
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_FILTER_MODE')
                  }}
                </label>
                <select
                  id="agent-filter-mode"
                  v-model="agentDraft.mode"
                  :disabled="agentLoading"
                  class="h-9 w-full rounded-md border border-n-weak bg-n-solid-2 px-2 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-slate-6"
                >
                  <option value="">
                    {{
                      t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_MODE_ALL')
                    }}
                  </option>
                  <option value="live">
                    {{
                      t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_MODE_LIVE')
                    }}
                  </option>
                  <option value="test">
                    {{
                      t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_MODE_TEST')
                    }}
                  </option>
                </select>
              </div>
              <div class="min-w-0">
                <Input
                  v-model="agentDraft.model"
                  type="text"
                  size="sm"
                  :label="t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_MODEL')"
                  :disabled="agentLoading"
                  :placeholder="
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_MODEL')
                  "
                />
              </div>
              <div class="min-w-0">
                <Input
                  v-model="agentDraft.created_after"
                  type="date"
                  size="sm"
                  :label="
                    t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_DATE_FROM')
                  "
                  :disabled="agentLoading"
                />
              </div>
              <div class="min-w-0">
                <Input
                  v-model="agentDraft.created_before"
                  type="date"
                  size="sm"
                  :label="t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_DATE_TO')"
                  :disabled="agentLoading"
                />
              </div>
              <div class="flex flex-wrap gap-2 justify-start xl:justify-end">
                <Button
                  size="sm"
                  :disabled="agentLoading"
                  @click="applyAgentFilters"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_APPLY') }}
                </Button>
                <Button
                  size="sm"
                  variant="faded"
                  :disabled="agentLoading"
                  @click="resetAgentFilters"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.FILTER_RESET') }}
                </Button>
              </div>
            </div>
          </div>

          <div v-if="agentLoading" class="flex justify-center py-10">
            <SpinnerLoader :size="32" class="text-n-blue-9" />
          </div>

          <template v-else>
            <div
              v-if="agentRows.length === 0"
              class="px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_EMPTY') }}
            </div>

            <div v-else class="overflow-x-auto">
              <table class="w-full min-w-[800px] text-left text-sm">
                <thead
                  class="bg-n-slate-3 text-n-slate-11 dark:bg-n-slate-2 uppercase tracking-wide text-xs"
                >
                  <tr>
                    <th class="px-4 py-2 font-medium">
                      {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_DATE') }}
                    </th>
                    <th class="px-4 py-2 font-medium">
                      {{
                        t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_COL_AGENT')
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium">
                      {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_MODEL') }}
                    </th>
                    <th class="px-4 py-2 font-medium">
                      {{
                        t('GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_COL_MODE')
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium">
                      {{
                        t(
                          'GENERAL_SETTINGS.AI_BILLING_PAGE.AI_AGENT_COL_STATUS'
                        )
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{
                        t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_PROMPT_TOKENS')
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{
                        t(
                          'GENERAL_SETTINGS.AI_BILLING_PAGE.COL_COMPLETION_TOKENS'
                        )
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{
                        t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_TOTAL_TOKENS')
                      }}
                    </th>
                    <th class="px-4 py-2 font-medium text-right">
                      {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.COL_AI_CREDITS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak dark:divide-n-slate-6">
                  <tr
                    v-for="row in agentRows"
                    :key="row.id"
                    class="text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    <td class="px-4 py-2 whitespace-nowrap">
                      {{ formatDateTime(row.created_at) }}
                    </td>
                    <td class="px-4 py-2 text-xs">
                      {{ row.ai_agent_name || '—' }}
                    </td>
                    <td class="px-4 py-2 font-mono text-xs">
                      {{ row.model || '—' }}
                    </td>
                    <td class="px-4 py-2 text-xs capitalize">
                      {{ row.mode }}
                    </td>
                    <td class="px-4 py-2 text-xs capitalize">
                      {{ row.status }}
                    </td>
                    <td class="px-4 py-2 text-right tabular-nums">
                      {{ formatInt(row.prompt_tokens) }}
                    </td>
                    <td class="px-4 py-2 text-right tabular-nums">
                      {{ formatInt(row.completion_tokens) }}
                    </td>
                    <td class="px-4 py-2 text-right tabular-nums font-medium">
                      {{ formatInt(row.total_tokens) }}
                    </td>
                    <td
                      class="px-4 py-2 text-right tabular-nums text-n-slate-12"
                    >
                      {{ formatCredits(row.ai_credits_used) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="agentMeta.total_pages > 1"
              class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-n-weak dark:border-n-slate-6"
            >
              <span class="text-xs text-n-slate-11">
                {{
                  paginationLabel(
                    agentMeta,
                    'GENERAL_SETTINGS.AI_BILLING_PAGE.PAGINATION'
                  )
                }}
              </span>
              <div class="flex gap-2">
                <Button
                  size="sm"
                  variant="faded"
                  :disabled="agentMeta.current_page <= 1"
                  @click="agentPrev"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.PREVIOUS') }}
                </Button>
                <Button
                  size="sm"
                  variant="faded"
                  :disabled="agentMeta.current_page >= agentMeta.total_pages"
                  @click="agentNext"
                >
                  {{ t('GENERAL_SETTINGS.AI_BILLING_PAGE.NEXT') }}
                </Button>
              </div>
            </div>
          </template>
        </section>
      </template>
    </div>
  </div>
</template>
